import type { RcFile } from 'antd/es/upload';
import type { DocumentType } from '../../types';

export const MAX_VIDEO_SECONDS = 30;
export const MAX_IMAGE_MB = 5;
export const MAX_VIDEO_MB = 25;

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const VIDEO_TYPES = ['video/mp4', 'video/webm'];

/**
 * Reads duration from the file's metadata without uploading it. The server cannot measure duration
 * without a media toolchain, so this is where the 30-second rule is actually enforced — it stops an
 * oversized clip before it is sent rather than after.
 */
function readVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file could not be read as a video.'));
    };
    video.src = url;
  });
}

/** Returns a human-readable problem, or null when the file is acceptable. */
export async function validateDocument(file: RcFile, type: DocumentType): Promise<string | null> {
  const isVideo = type === 'agreement-video';
  const allowed = isVideo ? VIDEO_TYPES : IMAGE_TYPES;
  const maxMb = isVideo ? MAX_VIDEO_MB : MAX_IMAGE_MB;

  if (!allowed.includes(file.type)) {
    return isVideo ? 'Please choose an MP4 or WebM video.' : 'Please choose a JPG, PNG or WebP image.';
  }

  if (file.size > maxMb * 1024 * 1024) {
    return `File must be under ${maxMb} MB (this one is ${(file.size / 1024 / 1024).toFixed(1)} MB).`;
  }

  if (isVideo) {
    try {
      const seconds = await readVideoDuration(file);
      if (seconds > MAX_VIDEO_SECONDS + 0.5) {
        return `Video must be ${MAX_VIDEO_SECONDS} seconds or less (this one is ${Math.round(seconds)}s).`;
      }
    } catch (err) {
      return (err as Error).message;
    }
  }

  return null;
}
